import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  FileCheck, 
  X, 
  AlertCircle,
  CheckCircle2,
  Shield,
  Calendar,
  FileText,
  Loader2,
  Info,
  ChevronDown
} from 'lucide-react';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

const CREDENTIAL_TYPES = {
  driver: [
    { id: 'background_check', name: 'Background Check', description: 'Criminal background check and screening' },
    { id: 'drug_screen', name: 'Drug Screen', description: 'Drug and alcohol screening results' },
    { id: 'dmv_record', name: 'DMV/MVR Record', description: 'Motor vehicle record and driving history' },
    { id: 'drivers_license', name: "Driver's License", description: 'Valid driver\'s license' },
    { id: 'medical_certificate', name: 'Medical Certificate', description: 'DOT medical examiner certificate' },
    { id: 'training_cert', name: 'Training Certificate', description: 'CPR, defensive driving, or other training' },
    { id: 'vehicle_insurance', name: 'Personal Vehicle Insurance', description: 'Personal auto insurance for rideshare' }
  ],
  provider: [
    { id: 'business_license', name: 'Business License', description: 'State business license or registration' },
    { id: 'dot_authority', name: 'DOT/MC Authority', description: 'USDOT number and operating authority' },
    { id: 'insurance_coi', name: 'Certificate of Insurance', description: 'Commercial auto liability insurance COI' },
    { id: 'general_liability', name: 'General Liability Insurance', description: 'General liability insurance certificate' },
    { id: 'workers_comp', name: 'Workers Compensation', description: 'Workers compensation insurance' },
    { id: 'npi_number', name: 'NPI Number', description: 'National Provider Identifier' },
    { id: 'medicaid_enrollment', name: 'Medicaid Enrollment', description: 'State Medicaid provider enrollment' },
    { id: 'vehicle_registration', name: 'Vehicle Registration', description: 'Vehicle registration document' },
    { id: 'vehicle_inspection', name: 'Vehicle Inspection', description: 'Annual vehicle inspection certificate' },
    { id: 'ada_certification', name: 'ADA Certification', description: 'ADA compliance certification for vehicles' },
    { id: 'hipaa_baa', name: 'HIPAA BAA Agreement', description: 'Business Associate Agreement' }
  ],
  broker: [
    { id: 'broker_license', name: 'Broker License', description: 'Healthcare transportation broker license' },
    { id: 'accreditation', name: 'Accreditation Certificate', description: 'URAC or other accreditation' },
    { id: 'insurance_coi', name: 'Certificate of Insurance', description: 'Commercial liability insurance COI' },
    { id: 'hipaa_baa', name: 'HIPAA BAA Agreement', description: 'Business Associate Agreement' }
  ],
  patient: [
    { id: 'insurance_card', name: 'Insurance Card', description: 'Health insurance card (front and back)' },
    { id: 'medicaid_card', name: 'Medicaid Card', description: 'State Medicaid benefits card' },
    { id: 'medicare_card', name: 'Medicare Card', description: 'Medicare benefits card' },
    { id: 'medical_authorization', name: 'Medical Authorization', description: 'Transportation authorization from provider' },
    { id: 'pas_form', name: 'Prior Authorization (PAS)', description: 'Prior Authorization Service form' },
    { id: 'doctors_orders', name: "Doctor's Orders", description: 'Standing orders for medical transport' },
    { id: 'patient_id', name: 'Photo ID', description: 'Government-issued photo identification' },
    { id: 'discharge_papers', name: 'Discharge Papers', description: 'Hospital discharge documentation' }
  ]
};

export const CredentialUpload = ({ role = 'driver', onSuccess, onClose }) => {
  const { user } = useExchangeAuth();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggestedType, setSuggestedType] = useState(null);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    credential_type: '',
    name: '',
    description: '',
    issue_date: '',
    expiration_date: ''
  });

  const credentialTypes = CREDENTIAL_TYPES[role] || CREDENTIAL_TYPES.driver;

  // Handle file drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelection = async (selectedFile) => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Invalid file type. Please upload PDF, JPEG, PNG, or WebP files.');
      return;
    }
    
    // Validate file size (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setFile(selectedFile);
    
    // Auto-suggest credential type based on filename
    try {
      const res = await exchangeApi.suggestCredentialType(selectedFile.name);
      if (res.data.suggestions && res.data.suggestions.length > 0) {
        const suggestion = res.data.suggestions[0];
        setSuggestedType(suggestion);
        
        // Auto-fill if high confidence
        if (suggestion.confidence >= 0.7 && !formData.credential_type) {
          setFormData(prev => ({
            ...prev,
            credential_type: suggestion.type_id,
            name: suggestion.name
          }));
        }
      }
    } catch (error) {
      console.log('Could not get type suggestion');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      credential_type: type.id,
      name: prev.name || type.name
    }));
    setShowTypeDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (!formData.credential_type) {
      toast.error('Please select a credential type');
      return;
    }
    
    if (!formData.name) {
      toast.error('Please enter a credential name');
      return;
    }
    
    setUploading(true);
    
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('credential_type', formData.credential_type);
      uploadData.append('name', formData.name);
      uploadData.append('description', formData.description || '');
      if (formData.issue_date) {
        uploadData.append('issue_date', new Date(formData.issue_date).toISOString());
      }
      if (formData.expiration_date) {
        uploadData.append('expiration_date', new Date(formData.expiration_date).toISOString());
      }
      
      const response = await exchangeApi.uploadCredentialFile(uploadData);
      
      if (response.data.success) {
        toast.success('Credential uploaded successfully!');
        if (onSuccess) {
          onSuccess(response.data);
        }
        // Reset form
        setFile(null);
        setFormData({
          credential_type: '',
          name: '',
          description: '',
          issue_date: '',
          expiration_date: ''
        });
        setSuggestedType(null);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload credential');
    } finally {
      setUploading(false);
    }
  };

  const selectedType = credentialTypes.find(t => t.id === formData.credential_type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-neon-green/20 flex items-center justify-center">
            <Upload className="w-5 h-5 text-neon-green" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Upload Credential</h3>
            <p className="text-sm text-slate-400">Securely upload and store your documents</p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Drop Zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragActive 
              ? 'border-neon-green bg-neon-green/10' 
              : file 
                ? 'border-neon-green/50 bg-neon-green/5'
                : 'border-slate-600 hover:border-slate-500'
          }`}
        >
          <input
            type="file"
            onChange={(e) => e.target.files?.[0] && handleFileSelection(e.target.files[0])}
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="credential-file-input"
          />
          
          {file ? (
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 rounded-xl bg-neon-green/20 flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-neon-green" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">{file.name}</p>
                <p className="text-sm text-slate-400">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setSuggestedType(null);
                }}
                className="p-2 text-slate-400 hover:text-red-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div>
              <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-white font-medium mb-1">
                Drag and drop your file here
              </p>
              <p className="text-sm text-slate-400">
                or click to browse • PDF, JPEG, PNG, WebP • Max 10MB
              </p>
            </div>
          )}
        </div>

        {/* AI Suggestion */}
        {suggestedType && suggestedType.confidence >= 0.5 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="flex items-center space-x-3 p-3 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg"
          >
            <Info className="w-5 h-5 text-neon-cyan flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-slate-300">
                Detected: <span className="text-neon-cyan font-medium">{suggestedType.name}</span>
                <span className="text-slate-500 ml-2">({Math.round(suggestedType.confidence * 100)}% confidence)</span>
              </p>
            </div>
            {formData.credential_type !== suggestedType.type_id && (
              <button
                type="button"
                onClick={() => handleTypeSelect(suggestedType)}
                className="text-xs text-neon-cyan hover:underline"
              >
                Use this
              </button>
            )}
          </motion.div>
        )}

        {/* Credential Type */}
        <div className="relative">
          <label className="block text-sm text-slate-400 mb-2">Credential Type *</label>
          <button
            type="button"
            onClick={() => setShowTypeDropdown(!showTypeDropdown)}
            className="w-full input-dark flex items-center justify-between"
            data-testid="credential-type-select"
          >
            <span className={selectedType ? 'text-white' : 'text-slate-500'}>
              {selectedType?.name || 'Select credential type...'}
            </span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showTypeDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-20 w-full mt-2 py-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto"
              >
                {credentialTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeSelect(type)}
                    className={`w-full px-4 py-3 text-left hover:bg-white/5 transition-colors ${
                      formData.credential_type === type.id ? 'bg-neon-green/10' : ''
                    }`}
                  >
                    <p className="text-white font-medium">{type.name}</p>
                    <p className="text-xs text-slate-400">{type.description}</p>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">Credential Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., Driver's License - John Doe"
            className="w-full input-dark"
            required
            data-testid="credential-name-input"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm text-slate-400 mb-2">Description (Optional)</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Additional notes about this credential..."
            rows={2}
            className="w-full input-dark resize-none"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Issue Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleInputChange}
                className="w-full input-dark pl-10"
                data-testid="credential-issue-date"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Expiration Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="date"
                name="expiration_date"
                value={formData.expiration_date}
                onChange={handleInputChange}
                className="w-full input-dark pl-10"
                data-testid="credential-expiration-date"
              />
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="flex items-start space-x-3 p-4 bg-white/5 rounded-lg">
          <Shield className="w-5 h-5 text-neon-green flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-slate-300 font-medium">Secure Storage</p>
            <p className="text-xs text-slate-400">
              Your document is encrypted with AES-256 and verified on our blockchain ledger for tamper-proof integrity.
            </p>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={uploading || !file || !formData.credential_type}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="credential-upload-submit"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading & Encrypting...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Credential
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default CredentialUpload;
